import React, { useState, useEffect, useCallback } from 'react';
import Notification from './Notification';

// Create a notification context to make notifications accessible throughout the app
export const NotificationContext = React.createContext({
  showNotification: () => {},
  notifications: [],
  hideNotification: () => {}
});

// Custom hook to use notifications in components
export const useNotification = () => React.useContext(NotificationContext);

// Notification provider component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  
  // Function to show a notification
  const showNotification = useCallback((message, type = 'success', duration = 3000, action = null) => {
    const id = Date.now(); // Generate a unique ID for the notification
    
    const notification = {
      id,
      message,
      type,
      duration,
      action
    };
    
    setNotifications(prevNotifications => [...prevNotifications, notification]);
    
    // Automatically remove the notification after duration
    if (duration > 0) {
      setTimeout(() => {
        hideNotification(id);
      }, duration);
    }
    
    return id; // Return the ID so it can be referenced later
  }, []);
  
  // Function to hide a notification
  const hideNotification = useCallback((id) => {
    setNotifications(prevNotifications => 
      prevNotifications.filter(notification => notification.id !== id)
    );
  }, []);
  
  // Value to be provided by the context
  const contextValue = {
    showNotification,
    notifications,
    hideNotification
  };
  
  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      
      {/* Render the notification container */}
      {notifications.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full space-y-2">
          {notifications.map(notification => (
            <Notification
              key={notification.id}
              message={notification.message}
              type={notification.type}
              onClose={() => hideNotification(notification.id)}
              autoClose={false} // Auto-close is handled by the provider via setTimeout
              action={notification.action}
            />
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;